import { Box, Stack } from "@mui/material";
import { DamsAppAsset } from "../../assets/registry";
import { MainPageBase } from "../page-bases/MainPageBase";
import { useEffect, useState } from "react";
import { DamsEmptyPageProps } from "../page-bases";
import DamsTable from "../../components/Table/CollapsibleTable";
import { NoItemContent } from "../page-bases/NoItemContent";
import { TRowItem } from "../../components/Table/TableRow";
import { ModalBase } from "../../components/Modals/ModalBase";
import ModalItemInput from "../../components/Input/ModalItemInput";
import {
  CreateBatchInfo,
  IBatch,
  IPagination,
  IQueryPerams,
  PageActions,
} from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import { Field } from "../../utils/formSchema";
import { ExportModal } from "../../components/Modals/ExportModal";

import ActionModal from "../../components/Modals/ActionModal";
import { batchApi } from "../../services/settings/batch";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { DomainSelectInput } from "../../common-ui/select-input-models/DomainSelectInput";
import { DonorSelectInput } from "../../common-ui/select-input-models/DonorSelectInput";
import { DatePicker } from "../../components/calender";
import { usePagination } from "../../hooks/usePagination";
import { BadgeLabel } from "../../components/Label/BadgeLabel";
import useAppNavigation from "../../hooks/useAppNavigation";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import dayjs from "dayjs";
import { formatDate } from "../../utils/common";
import { BatchLogsFilter } from "../../common-ui/page-filters/BatchLogsFilter";
import appRoutePath from "../../config/path";
import { appConfig } from "../../config/config";
import ItemInput from "../../components/Input/ItemInput";

function BatchsMainPage() {
  const [pageState, setPageState] = useState("main");
  const [emptyState, setEmptyState] = useState<DamsEmptyPageProps>();
  const [dataset, setDataset] = useState<TRowItem[]>([]);

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();
  const {
    form,
    updateForm,
    updateInputField,
    fieldHasError,
    setSubmitAttempt,
    resetForm,
    isFormValid,
    getFormValues,
  } = useDmsForm<CreateBatchInfo>({
    domain_id: "",
    donor_id: "",
    remarks: "",
    batch_domain: "",
    received_date: "",
    quantity: 0,
  });
  const [isProcessing, setProcess] = useState(false);

  const [showExportModal, setShowExportModal] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(batchApi.getBatches);

  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const populateDataSet = (data: IBatch[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      received_date: formatDate(data.received_date),
      updated_at: data.updated_at ? formatDate(data.updated_at) : "Not Yet",
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async () => {
    try {
      const result = await batchApi.getBatches({ is_deleted: false });
      if (result?.data) {
        const formatData = result.data.map((data) => ({
          ...data,
          received_date: formatDate(data.received_date),
        }));
        populateDataSet(formatData, result.pagination);
      }
    } catch (err) {
      setEmptyState({ type: "empty-db" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const page_info = getEmptyPageInfo(pageState);

  async function handleAddRequest() {
    setSubmitAttempt(true);
    if (isFormValid()) {
      setProcess(true);
      const user_request =
        pageAction?.type === "add"
          ? batchApi.createBatch(getFormValues())
          : batchApi.updateBatch(pageAction?.id ?? "", getFormValues());

      const res = await user_request;
      if (res.success) {
        fetchData();
      }
      setProcess(false);
      updatePageAction({
        type: pageAction?.type,
        message: res.message,
        title: pageAction?.type === "edit" ? "Update Batch" : "New Batch",
        success: res.success,
      });
      openModel("alert");
      resetForm();
    }
  }

  function addNewItem() {
    resetForm();
    openModel("add");
    updatePageAction({
      type: "add",
    });
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: item.batch_number,
    });
  }

  function handleUpdateModel(item: TRowItem) {
    openModel("add");
    updatePageAction({
      id: item.id,
      name: item.batch_number,
      type: "edit",
      data: item,
    });
    updateForm({
      domain_id: { value: item.domain_id, isValid: true },
      donor_id: { value: item.donor_id, isValid: true },
      remarks: { value: item.remarks, isValid: true },
      batch_domain: { value: item.batch_domain, isValid: true },
      received_date: { value: item.received_date, isValid: true },
      quantity: { value: item.quantity, isValid: true },
    });
  }

  async function handleDeleteRequest() {
    setProcess(true);
    const res = await batchApi.deleteBatch(pageAction?.id ?? "");
    if (res.success) {
      fetchData();
    }
    setProcess(false);
    updatePageAction({
      title: res.success ? "Delete Batch" : "Delete Batch Failed",
      message: res.message,
      success: res.success,
    });
    openModel("alert");
  }

  function toggleAlertModal() {
    if (pageAction?.type === "add") {
      openModel("add");
    } else {
      closeModel();
    }
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function handleDomainSelect(item: Field, domain: string) {
    updateForm((p) => ({
      ...p,
      domain_id: item,
      batch_domain: { value: domain, isValid: true },
    }));
  }

  function handleDonorSelect(item: Field) {
    updateForm((p) => ({ ...p, donor_id: item }));
  }

  const handleDatePicker = (date: string) => {
    updateForm((p) => ({
      ...p,
      received_date: { value: date, isValid: true },
    }));
  };

  function openExportModal() {
    setShowExportModal(true);
  }

  function closeExportModal() {
    setShowExportModal(false);
  }

  async function handleExportCSV() {
    setIsExportingCSV(true);
    try {
      const response = await batchApi.exportBatches({
        file_format: "csv",
        ...querySet,
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split("/").pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace(
          "/api/v1",
          ""
        )}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: "GET",
          });

          if (!downloadResponse.ok) {
            throw new Error("Download failed");
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement("a");
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || "batches_export.csv";

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "CSV file has been downloaded successfully.",
            success: true,
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, "_blank");

          updatePageAction({
            title: "Export Initiated",
            message:
              "CSV export has been initiated. Check your downloads folder.",
            success: true,
          });
        }

        openModel("alert");
      } else {
        throw new Error("Invalid response from export API");
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "CSV export failed. Please try again.",
        success: false,
      });
      openModel("alert");
    } finally {
      setIsExportingCSV(false);
      closeExportModal();
    }
  }

  async function handleExportPDF() {
    setIsExportingPDF(true);
    try {
      const response = await batchApi.exportBatches({
        file_format: "pdf",
        ...querySet,
      });

      if (response.success && response.data?.download_url) {
        const downloadUrl = response.data.download_url;
        const filename = downloadUrl.split("/").pop();

        const fullDownloadUrl = `${appConfig.baseBackendUrl.replace(
          "/api/v1",
          ""
        )}${downloadUrl}`;

        try {
          const downloadResponse = await fetch(fullDownloadUrl, {
            method: "GET",
          });

          if (!downloadResponse.ok) {
            throw new Error("Download failed");
          }

          const blob = await downloadResponse.blob();

          const link = document.createElement("a");
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = filename || "batches_export.pdf";

          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          updatePageAction({
            title: "Export Successful",
            message: "PDF file has been downloaded successfully.",
            success: true,
          });
        } catch (downloadError) {
          window.open(fullDownloadUrl, "_blank");

          updatePageAction({
            title: "Export Initiated",
            message:
              "PDF export has been initiated. Check your downloads folder.",
            success: true,
          });
        }

        openModel("alert");
      } else {
        throw new Error("Invalid response from export API");
      }
    } catch (error) {
      updatePageAction({
        title: "Export Failed",
        message: "PDF export failed. Please try again.",
        success: false,
      });
      openModel("alert");
    } finally {
      setIsExportingPDF(false);
      closeExportModal();
    }
  }

  const appNavigation = useAppNavigation();
  const renderGoAddAssetPage = ({ item }: { item: TRowItem }) => {
    const toggleOnClick = () => {
      appNavigation({
        path: appRoutePath.ADD_ASSET_REGISTRY_PAGE,
        options: { state: { batch_id: item.id } },
      });
    };
    return (
      <Box>
        {" "}
        <BadgeLabel
          onClick={toggleOnClick}
          title={"Add Asset"}
          colorType="blue"
        />
      </Box>
    );
  };

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={batchApi.deleteBatch}
          idKey={"id"}
        />
      </Stack>
    );
  };

  return (
    <MainPageBase
      title={"Batches"}
      pageState={pageState}
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText="Add Batch"
      exportBtnOnClick={openExportModal}
      exportBtnText={"Export"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"} spacing={1}>
            <BatchLogsFilter<IBatch[]>
              ftn={batchApi.getBatches}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Batches"
              tableTitleBadge={
                selectedRowItems.length === 0
                  ? undefined
                  : selectedRowItems.length + " selected"
              }
              tableSubtitle=""
              numTableRecords="25"
              tableHeaders={columns}
              tableKeys={tkeys}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              data={dataset}
              extraRowComponent={renderGoAddAssetPage}
              onTableItemEdit={handleUpdateModel}
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              useTableCheckbox
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              selectedRowItems={selectedRowItems}
              headerDropDownMenu={renderHeaderDropDownMenu}
            />

            {/* Export Modal */}
            <ExportModal
              show={showExportModal}
              onClose={closeExportModal}
              onExportCSV={handleExportCSV}
              onExportPDF={handleExportPDF}
              isExportingCSV={isExportingCSV}
              isExportingPDF={isExportingPDF}
              title="Export Data"
              subtitle="Choose your preferred format to export batches data"
            />

            {/* Add Modal */}
            <ModalBase
              open={models.showAddModel}
              title={
                pageAction?.type === "add" ? "Add New Batch" : "Edit Batch"
              }
              subtitle={""}
              type={"add-item"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.Programmes}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt={
                pageAction?.type === "add" ? "Add Batch" : "Edit Batch"
              }
              onRightBtnPress={handleAddRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            >
              <Stack spacing={2}>
                <DatePicker
                  width={360}
                  handleOnchange={handleDatePicker}
                  label="Date Recieved"
                  error={fieldHasError("received_date")}
                  errorMessage={form.received_date.errorMessage}
                  value={dayjs(form.received_date.value)}
                />

                <DonorSelectInput
                  placeholder="Select Donor"
                  label="Donor"
                  handleChange={handleDonorSelect}
                  error={!form.donor_id.isValid && form.submitAttempt}
                  value={form.donor_id.value}
                />

                <ItemInput
                  label="Quantity"
                  placeholder="Enter Quantity"
                  handleChange={updateInputField("quantity")}
                  value={form.quantity.value}
                  error={fieldHasError("quantity")}
                  errorMessage={form.quantity.errorMessage}
                  size="full"
                />
                <ModalItemInput
                  label="Purpose"
                  placeholder="Enter reason batch is being added"
                  handleChange={updateInputField("remarks")}
                  value={form.remarks.value}
                  error={fieldHasError("remarks")}
                  errorMessage={form.remarks.errorMessage}
                  multiline
                  rows={4}
                />
                <DomainSelectInput
                  label="Domain Name"
                  placeholder="Select Domain"
                  handleChange={handleDomainSelect}
                  value={{
                    domain_name: form.batch_domain.value ?? undefined,
                    domain_id: form.domain_id.value ?? undefined,
                  }}
                />
              </Stack>
            </ModalBase>

            <ModalBase
              open={models.showDeleteModel}
              title={"Delete Batch"}
              subtitle={
                <Box>
                  Once you delete
                  <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>
                  , all related information will be lost forever. Are you sure
                  you want to proceed?
                </Box>
              }
              type={"action"}
              showHeader={true}
              leftIcon={DamsAppAsset.icons.delete_icon}
              leftBtnTxt="Cancel"
              onLeftBtnPress={closeModel}
              rightBtnTxt="Delete"
              onRightBtnPress={handleDeleteRequest}
              onRightBtnLoading={isProcessing}
              onClose={closeModel}
            />

            <ActionModal
              show={models.showAlertModel}
              title={pageAction?.title ?? ""}
              leftIcon={
                pageAction?.success === false
                  ? DamsAppAsset.icons.warning
                  : undefined
              }
              subtitle={pageAction?.message ?? ""}
              onLeftBtnPress={closeModel}
              leftBtnTxt={"close"}
              onRightBtnPress={toggleAlertModal}
              rightBtnTxt={pageAction?.type === "add" ? "Add New" : "continue"}
              onClosePress={closeModel}
            />
          </Stack>
        )}
      </Box>
    </MainPageBase>
  );
}

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Batches Found",
    message:
      'No Batches have been added yet. Click the "Add New" button to add a new Programme.',
  },

  "empty-search": {
    title: "No Batches Found",
    message:
      "No Batches found in your database. Please add a new Programme to find them in the Batches tab ",
  },
};

export default BatchsMainPage;

const tkeys = [
  "donor",
  "batch_domain",
  "batch_number",
  "quantity",
  "received_date",
];
const columns = [
  "Donor",
  "Batch Domain",
  "Batch Number",
  "Quantity",
  "Received Date",
];
const hkeys = ["remarks", "updated_at"];
const hcolumns = ["Remarks", "Last updated At"];
